import {
  IncidentType,
  AuthorizationFailedReason
} from "@moderrotech/servers-communication-library/src/types";

export const en = {
  topics: {
    headerTitle: "Topics",
    search: "Search",
    noTopics: "No topics found",
    notFound: "No topics matching pattern found",
    schedule: {
      closed: "We are closed"
    },
    customerInfoForm: {
      title: "Before we begin your video call...",
      description:
        "Before using video banking to apply your loan, please complete the form",
      optional: "Optional",
      buttons: {
        cancel: "Cancel",
        submit: "Start video call"
      }
    }
  },
  call: {
    calling: "Calling...",
    joining: "Joining the call",
    transferring: "Transferring the call",
    aspectRatio: "Zoom",
    cancel: "Cancel call",
    end: "End call",
    share: "Multi party call",
    document: "Document",
    actions: "Actions",
    closeActions: "Cancel",
    mute: "Mute",
    unmute: "Unmute",
    textMessage: "Text Message",
    queuePosition: (ctx: { list: Function }) => {
      const position = ctx.list(0);
      const isPositionAvailable = ctx.list(1);
      return `You are ${isPositionAvailable ? `#${position} ` : ""} in queue`;
    },
    ticket: "Ticket",
    camera: {
      enable: "Enable camera",
      disable: "Disable camera"
    },
    hangupRequest: {
      title: "Are you sure?",
      body: "Do you really want to disconnect?",
      yes: "Yes, hangup",
      no: "No, stay on call"
    },
    downloadFileRequest: {
      title: "Agent has shared a file with you",
      yes: "Download",
      no: "Dismiss"
    },
    openUrlRequest: {
      title: "Open URL",
      body: "The agent wants to open a URL in new tab",
      yes: "Open URL",
      no: "Cancel"
    },
    lowPerformanceNotification: {
      title:
        "Looks your device has low performance. Please, do not use camera to avoid app crashing",
      gotIt: "Got it"
    },
    signaturePad: {
      submit: "Submit",
      clear: "Clear",
      cancel: "Cancel"
    },
    paintover: {
      submit: "Submit",
      clear: "Clear",
      cancel: "Cancel"
    },
    textMessageForm: {
      placeholder: "Message"
    },
    recordTranslator: {
      transcribing: "Speak, please"
    },
    cameraSnapshot: {
      title: "Camera snapshot"
    },
    mediaInputsAccessNote: {
      title: "Please allow microphone and camera access",
      description: "Look for a small popup near your browser's address bar."
    },
    animatedCharacter: {
      listening: {
        title: "Agent is listening",
        description: "You can speak now. Say your question or request."
      },
      thinking: {
        title: "Agent is thinking",
        description: "Please wait while the agent finds an answer."
      }
    },
    success: {
      document: "Document has been successfully sent.",
      feedback: "A feedback has been sent",
      textMessage: "The text message has been sent",
      signaturePad: "The signature has been sent",
      paintover: "The image has been sent",
      textCopy: "The text has been copied to clipboard",
      imageCopy: "The image has been copied to clipboard",
      callSharing: "The link has been copied to clipboard"
    },
    error: {
      base: "Something went wrong. Please, reload the page",
      mediaInputsAccess:
        "Can not get access to microphone and/or camera. Please, enable them in settings",
      join: "Can not join to the call",
      document: "Can not send document",
      noExperts: "No agents available",
      noTopic: "Topic is no longer available",
      queueSuspended:
        "Queue suspended<br>We’re not able to take new calls at the moment. Please try again later.",
      queueOverloaded:
        "Queue overloaded<br>We’re experiencing a high volume of calls. Please try again later.",
      timeout: "Waiting for an agent too long",
      documentSize: "The document is too large",
      feedback: "Something went wrong",
      noRoom: "The appointment has expired or was canceled by doctor",
      textMessage: "The text message can not be sent",
      signaturePad: "The signature can not be sent",
      paintover: "The image sending error",
      textCopy: "Can not copy text",
      imageCopy: "Can not copy image",
      callSharing: "Can not share link",
      recordTranslation: {
        play: "Can not play translation audio",
        streamGetting: "Can not get audio stream",
        transcription: "Can not transcribe the record",
        translation: "Can not translate the record",
        unsupportedLanguage:
          "Selected unsupported for YOUR audio translation language. But agent`s audio can be translated on this language"
      },
      geolocation: "Can not get geolocation",
      cameraSnapshot: "Can not take camera snapshot"
    },
    onHold: {
      title: "On hold.",
      description: "The agent will get back to you shortly."
    },
    remoteActionRequest: {
      shareFile: {
        title: "The agent wants to share a file from your device. Allow?",
        yes: "Yes",
        no: "No"
      },
      screenSharing: {
        title: "The agent wants to see your screen. Allow?",
        yes: "Yes",
        no: "No"
      }
    },
    settings: {
      title: "Settings",
      labels: {
        camera: "Camera",
        microphone: "Microphone"
      }
    }
  },
  thank: {
    toTopics: "Return to topics",
    redirect: "To appointment"
  },
  ticket: {
    creationRequest: {
      title: (ctx: { list: Function }) => {
        const incidentType = ctx.list(0);
        return incidentType === IncidentType.queueTimeout
          ? "Timed out."
          : "No agents available.";
      },
      body: "Want to leave them a message?",
      yes: "Yes",
      no: "No"
    },
    form: {
      labels: {
        phone: "Phone",
        email: "Email",
        message: "Message"
      },
      buttons: {
        cancel: "Cancel",
        submit: "Send a message"
      }
    }
  },
  feedback: {
    form: {
      fields: {
        email: {
          label: "From:",
          validation: {
            label: "email"
          }
        },
        message: {
          label: "message",
          placeholder: "Message",
          validation: {
            label: "message"
          }
        }
      },
      buttons: {
        submit: "Send",
        cancel: "Cancel",
        open: "Add feedback message..."
      },
      notifications: {
        success: "Bug report has been sent"
      }
    }
  },
  error: {
    connection: {
      title: (ctx: { list: Function }) => {
        const isOnCall = ctx.list(0);
        return isOnCall
          ? "The Agent is staying with you and will talk to you in a moment."
          : "Service temporarily unavailable";
      },
      description: (ctx: { list: Function }) => {
        const isOnCall = ctx.list(0);
        return isOnCall
          ? "We are about to restore your connection."
          : "Connection has been lost. Reconnecting...";
      },
      code: "code: 503"
    },
    login: {
      credentials: "Wrong username or password",
      default: "Something went wrong"
    },
    unpublished: {
      title: "The application does not exist.",
      description:
        "It might have been temporarily unpublished or deleted permanently.",
      code: "code: 404"
    },
    authorization: {
      title: "Access forbidden",
      description: (ctx: { list: Function }) => {
        const reason = ctx.list(0) as
          | AuthorizationFailedReason.geofencingFailed
          | AuthorizationFailedReason.noLicense
          | AuthorizationFailedReason.remStopped
          | AuthorizationFailedReason.noPtpcResponse;
        return {
          [AuthorizationFailedReason.geofencingFailed]:
            "The application is not available in your country or region.",
          [AuthorizationFailedReason.noLicense]: "No assigned license.",
          [AuthorizationFailedReason.remStopped]: "Service unavailable.",
          [AuthorizationFailedReason.noPtpcResponse]: "No server response."
        }[reason];
      },
      code: "code: 403"
    },
    network: {
      title: "Please make sure you have Internet access"
    }
  },
  validation: {
    phone: "The {field} field must be a valid phone number",
    dni: "Wrong ID, invalid format",
    nie: "Wrong ID, invalid format"
  }
};
